import { ApiResult, PaginatedResponse, request, safeRequest } from '@synergy/api';
import { Category, DefaultMapping, LocalizedString } from '@synergy/models';

import { CategoryRequest } from './models/category-request';

export interface CategorySearchHit {
    id: number;
    translations: LocalizedString;
    ancestors: { id: number; translations: LocalizedString }[];
}

export class CategoryApi {
    constructor(protected baseUrl: string) {}

    public cgetCategories(): Promise<ApiResult<PaginatedResponse<Category>>> {
        return safeRequest(
            () => request.get(`${this.baseUrl}/categories`, { params: new URLSearchParams({ limit: '15000' }) }),
            response => {
                response.data.items = response.data.items.map(Category.fromPlainObject);
                return response.data;
            }
        );
    }

    /**
     * Categories whose name contains the term, each with the categories above it
     * — enough to show where a hit sits and to tick its parents along.
     */
    public searchCategories(search: string, abortSignal?: AbortSignal): Promise<ApiResult<CategorySearchHit[]>> {
        return safeRequest(
            () =>
                request.get(`${this.baseUrl}/categories`, {
                    params: new URLSearchParams({ search, limit: '40' }),
                    signal: abortSignal,
                }),
            response => response.data.items
        );
    }

    public getAllCategories = (parentId?: number): Promise<ApiResult<Category[]>> => {
        const rootOnly = parentId === undefined;
        return safeRequest(
            () => request.get(`${this.baseUrl}/all-categories`, { params: { parentId, rootOnly } }),
            response => response.data
        );
    };

    public deleteCategory = async (categoryId: number): Promise<ApiResult<Category>> => {
        return safeRequest(
            () => request.delete(`${this.baseUrl}/categories/${categoryId}`),
            response => response.data
        );
    };

    public sendNewCategory = async (data: CategoryRequest): Promise<ApiResult<Category>> => {
        return safeRequest(
            () => request.post(`${this.baseUrl}/categories`, data),
            response => response.data
        );
    };

    public updateCategory = async (id: number, data: CategoryRequest): Promise<ApiResult<Category>> => {
        return safeRequest(
            () => request.patch(`${this.baseUrl}/categories/${id}`, data),
            response => response.data
        );
    };

    public updateDefaultMapping = async (mapping: DefaultMapping): Promise<ApiResult<DefaultMapping<Category>>> => {
        return safeRequest(
            () =>
                request.post(`${this.baseUrl}/categories/${mapping.id}/mapping`, {
                    defaultMapping: mapping.defaultMapping,
                }),
            response => response.data
        );
    };

    public getDefaultMapping(): Promise<ApiResult<DefaultMapping<Category>[]>> {
        return safeRequest(
            () =>
                request.get<PaginatedResponse<DefaultMapping<Category>>>(`${this.baseUrl}/categories/mapping`, {
                    params: new URLSearchParams({ limit: '15000' }),
                }),
            response => {
                return response.data.items;
            }
        );
    }

    public hideCategory = async (categoryId: number): Promise<ApiResult<null>> => {
        return safeRequest(
            () => request.post(`${this.baseUrl}/categories/${categoryId}/hide`),
            () => null
        );
    };

    public showCategory = async (categoryId: number): Promise<ApiResult<null>> => {
        return safeRequest(
            () => request.post(`${this.baseUrl}/categories/${categoryId}/show`),
            () => null
        );
    };
}
